import { useCallback } from 'react';
import { usePdfExport } from '../common/ai-chat/message-atoms/usePdfExport';
import { telemetry } from '../../telemetry';
import { useSelector } from 'react-redux';
import { selectChatsDetails } from './agentsChatsReducer';
import { createRoot } from 'react-dom/client';
import { AiChat } from '../common/ai-chat/aiChatTypes';
import { DataTableVariant } from '../common/table/TableTypes';
import { AiChatMessages } from './../common/ai-chat/AiChatMessages';
import { useAiAgentApiFacade } from '../../apis/facades/useAiAgentApiFacade';

const createTempChatContainer = async (chatData: AiChat): Promise<HTMLElement | null> => {
  // Calculate width that fits PDF page
  const jsPDF = (await import('jspdf')).default;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const maxContentWidth = pdfWidth - 30; // 15mm padding * 2
  const maxWidthInPixels = maxContentWidth / (25.4 / 96); // Convert mm to pixels

  return new Promise((resolve) => {
    const tempDiv = document.createElement('div');
    tempDiv.id = `chat-messages-container-${chatData.id}`;

    // Style for proper rendering
    Object.assign(tempDiv.style, {
      position: 'fixed',
      top: '0px',
      left: '0px',
      width: `${maxWidthInPixels}px`,
      height: 'auto',
      zIndex: '9999',
      pointerEvents: 'none',
      opacity: '0.01',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5',
      color: '#000',
      backgroundColor: '#fff',
      overflow: 'visible',
    });

    document.body.appendChild(tempDiv);

    const root = createRoot(tempDiv);
    const containerRef = { current: tempDiv };

    try {
      root.render(
        <AiChatMessages
          containerRef={containerRef}
          messages={chatData.aiChatDetails.messages}
          renderLoader={false}
          renderInitialFetchMessage={false}
          renderChatPaywallBlockMessage={false}
          spacerFix={0}
          activeChatId={chatData.id}
          chatName={chatData.name}
          dataTableVariant={DataTableVariant.medium}
        />,
      );

      // Wait for rendering to complete
      setTimeout(() => {
        void tempDiv.offsetHeight; // Force layout calculation
        setTimeout(() => {
          (tempDiv as any).__reactRoot = root;
          resolve(tempDiv);
        }, 200);
      }, 800);
    } catch (error) {
      root.unmount();
      resolve(null);
    }
  });
};

export const useChatPdfExport = () => {
  const { exportToPdf } = usePdfExport();
  const chatsDetails = useSelector(selectChatsDetails);
  const { get_chat } = useAiAgentApiFacade();

  const exportChatToPdf = useCallback(
    async (chatId: string, chatName?: string) => {
      try {
        // Check if chat is currently rendered
        const chatContainer = document.getElementById(`chat-messages-container-${chatId}`);
        if (chatContainer) {
          return await exportToPdf(chatContainer, chatName);
        }

        // Get chat data from store or API
        let chatData = chatsDetails?.[chatId]?.data;
        if (!chatData) {
          const fetchedChat = await get_chat(chatId);
          chatData = { ...fetchedChat, agentType: 'Documentation' as any }; //adding agentType just for typescript, we dont need it in this hook
        }

        if (!chatData) {
          throw new Error(`Chat data not found for ID: ${chatId}`);
        }

        // Create temporary container and export
        const tempContainer = await createTempChatContainer(chatData);
        if (!tempContainer) {
          throw new Error('Failed to create temporary chat container');
        }

        try {
          // Make container visible for export
          Object.assign(tempContainer.style, {
            opacity: '1',
            position: 'static',
            zIndex: 'auto',
          });
          void tempContainer.offsetHeight; // Force layout calculation

          await new Promise((resolve) => setTimeout(resolve, 200));
          return await exportToPdf(tempContainer, chatName);
        } finally {
          // Cleanup
          const reactRoot = (tempContainer as any).__reactRoot;
          if (reactRoot) reactRoot.unmount();
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
        }
      } catch (error) {
        telemetry.captureError(new Error('Chat PDF export failed'), error);
        return false;
      }
    },
    [exportToPdf, chatsDetails, get_chat],
  );

  return { exportChatToPdf };
};
